/**
 * Плагин для автоматической регистрации Vue компонентов
 *
 * Конфигурация директорий находится внутри этого файла (AUTOLOADER_CONFIG)
 *
 * ВАЖНО: При добавлении новой директории нужно:
 * 1. Добавить в AUTOLOADER_CONFIG.directories
 * 2. Добавить import.meta.glob ниже (из-за ограничений Vite)
 * 3. Перезапустить dev-сервер
 *
 * Использование:
 * ```js
 * import AutoRegisterPlugin from '@/core/AutoRegisterPlugin.js';
 * app.use(AutoRegisterPlugin);
 * ```
 */
import { defineAsyncComponent } from 'vue';

/**
 * Конфигурация автоимпорта компонентов
 */
export const AUTOLOADER_CONFIG = {
	directories: [
		{
			path: '../../lib',
			pattern: '../../lib/**/*.vue',
			outputTypes: '../types/d.ts/lib-components.d.ts',
			lazyLoading: false,
		},
		{
			path: '../services',
			pattern: '../services/**/*.vue',
			outputTypes: '../types/d.ts/core-components.d.ts',
			lazyLoading: false,
		}
	],
	excludeFiles: ['generate-types.js', 'AutoRegisterPlugin.js', 'index.ts'],
	excludeDirs: ['__tests__', '__mocks__', 'drafts', 'deprecated', 'pages', 'samples'],
};

/**
 * Извлекает имя компонента из пути файла
 */
function getComponentName(path) {
	return path.split('/').pop().replace(/\.\w+$/, '');
}

export default {
	install(app) {
		const registeredComponents = new Set();
		const duplicates = [];

		// ВАЖНО: import.meta.glob требует литеральные строки
		// Паттерны должны совпадать с AUTOLOADER_CONFIG.directories
		
		// UI библиотека
		const libComponents = import.meta.glob('../../lib/**/*.vue', { eager: true });
		const libConfig = AUTOLOADER_CONFIG.directories.find(d => d.path === '../../lib');
		registerComponents(libComponents, libConfig.lazyLoading);
		
		// Компоненты ядра
		const coreComponents = import.meta.glob('../services/**/*.vue', { eager: true });
		const coreConfig = AUTOLOADER_CONFIG.directories.find(d => d.path === '../services');
		registerComponents(coreComponents, coreConfig.lazyLoading);
		
		// При добавлении новой директории:
		// const newComponents = import.meta.glob('./path/**/*.vue', { eager: !lazyLoading });
		// const newConfig = AUTOLOADER_CONFIG.directories.find(d => d.path === './path');
		// registerComponents(newComponents, newConfig.lazyLoading);

		/**
		 * Регистрирует компоненты
		 */
		function registerComponents(components, lazyLoading) {
			Object.entries(components).forEach(([path, moduleOrLoader]) => {
				const componentName = getComponentName(path);

				// Проверка на дубликаты
				if (registeredComponents.has(componentName)) {
					duplicates.push({ name: componentName, path });
					return;
				}

				// Регистрируем компонент
				if (lazyLoading) {
					// Lazy: функция-загрузчик
					app.component(componentName, defineAsyncComponent(moduleOrLoader));
				} else {
					// Eager: модуль уже загружен
					app.component(componentName, moduleOrLoader.default);
				}
				
				registeredComponents.add(componentName);
				
				/*
				const loadType = lazyLoading ? '💤 lazy' : '⚡ eager';
				console.debug(`✅ AutoRegister [${loadType}]: ${componentName}`);
				*/
			});
		}

		// Проверка на дубликаты - ФАТАЛЬНАЯ ОШИБКА
		if (duplicates.length > 0) {
			console.error(
				`\n❌ ФАТАЛЬНАЯ ОШИБКА: Обнаружены дубликаты имён компонентов!\n` +
				`   Это приведёт к непредсказуемому поведению приложения.\n`
			);
			duplicates.forEach(({ name, path }) => {
				console.error(`   ❌ Компонент "${name}" дублируется в: ${path}`);
			});
			console.error(`\n   Необходимо переименовать компоненты или удалить дубликаты.\n`);
			
			throw new Error(
				`AutoRegisterPlugin: Найдено ${duplicates.length} дубликат(ов) имён компонентов. ` +
				`Проверьте консоль для деталей.`
			);
		}
		
		/*
		// Итоговая статистика
		console.log(
			`\n🎨 AutoRegister завершён успешно:\n` +
			`   📦 Зарегистрировано компонентов: ${registeredComponents.size}`
		);
		 */
	}
};